/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TablePagination from '@material-ui/core/TablePagination'
import Paper from '@material-ui/core/Paper'
import IconButton from '@material-ui/core/IconButton'
import { ChevronLeft, ChevronRight, FirstPage, LastPage } from '@material-ui/icons'
import { CircularProgress } from '@material-ui/core'

import {
  montserratStyle,
  tableCellStyle,
  tableHeaderStyle,
  tablePaginationStyle,
  tableSubHeaderStyle,
} from '../styles'

interface StoreColumn {
  store?: string
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  columns: { label: string; key: string }[]
}

export interface RowData {
  label: string
  type?: string
  [key: string]: string | number | undefined
}

interface ComparisonTableProps {
  title?: string
  header?: string
  stores: StoreColumn[]
  data: RowData[]
  downloadButton?: React.ReactNode
  isLoading?: boolean
  error?: string | null
  emptyMessage?: string
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  globalColumn?: { label: string; key: string }
  // Controlled pagination props
  rowsPerPage?: number
  currentPage?: number
  totalPages?: number
  totalCount?: number
  onPageChange?: (page: number) => void
  onRowsPerPageChange?: (rowsPerPage: number) => void
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  title,
  header,
  stores,
  data,
  downloadButton,
  isLoading,
  error,
  emptyMessage,
  globalColumn,
  totalPages = 1,
  rowsPerPage = 5,
  currentPage = 1,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {title && (
          <div className="font-bold mb-4 text-base" style={montserratStyle}>
            {title}
          </div>
        )}
        <div className="flex justify-center items-center h-32">
          <CircularProgress size={40} />
        </div>
      </div>
    )
  }

  // Handle error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {title && (
          <div className="font-bold mb-4 text-base" style={montserratStyle}>
            {title}
          </div>
        )}
        <div className="flex justify-center items-center h-32">
          <span className="text-red-500" style={{ ...montserratStyle }}>
            {error}
          </span>
        </div>
      </div>
    )
  }

  // Handle empty state
  if (data.length === 0 && !isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        {title && (
          <div className="font-bold mb-4 text-base" style={montserratStyle}>
            {title}
          </div>
        )}
        <div className="flex justify-center items-center h-32">
          <span className="text-gray-600" style={{ ...montserratStyle }}>
            {emptyMessage}
          </span>
        </div>
      </div>
    )
  }

  // Calculate pagination
  // If totalCount is provided, we assume server-side pagination and do NOT slice data on the client.
  // Otherwise, fall back to client-side pagination based on the full data set.
  const isServerPaginated = !!onPageChange && typeof totalCount === 'number'
  const totalItems = data.length

  let finalTotalPages = totalPages
  if (!isServerPaginated && onPageChange) {
    finalTotalPages = Math.ceil(totalItems / rowsPerPage)
  }

  const shouldSliceClientSide = !isServerPaginated && !!onPageChange
  const startIndex = shouldSliceClientSide ? (currentPage - 1) * rowsPerPage : 0
  const endIndex = shouldSliceClientSide ? startIndex + rowsPerPage : totalItems
  const paginatedData = shouldSliceClientSide ? data.slice(startIndex, endIndex) : data

  return (
    <div
      className="bg-white rounded-lg shadow p-6"
      style={{ width: '100%', maxWidth: '91vw', overflowX: 'auto', boxSizing: 'border-box' }}
    >
      <div className="flex justify-between items-center mb-6">
        {title && (
          <div className="font-bold mb-4 text-base" style={{ ...montserratStyle }}>
            {title}
          </div>
        )}
        {downloadButton && downloadButton}
      </div>

      <TableContainer component={Paper} style={{ boxShadow: 'none', width: '100%' }}>
        <Table>
          <TableHead>
            {/* Store headers row */}
            <TableRow>
              {paginatedData.length > 0 && (
                <TableCell style={{ border: 'none', fontWeight: 'bold', ...montserratStyle }}>
                  {header}
                </TableCell>
              )}
              {globalColumn && (
                <TableCell
                  colSpan={1}
                  style={{ border: 'none', fontWeight: 'bold', ...montserratStyle }}
                >
                  {globalColumn.label}
                </TableCell>
              )}
              {stores.map((store, idx) =>
                store.store ? (
                  <TableCell
                    key={store.store}
                    colSpan={store.columns.length}
                    style={tableHeaderStyle(idx)}
                  >
                    {store.store}
                  </TableCell>
                ) : null,
              )}
            </TableRow>
            {/* Column headers row */}
            <TableRow>
              <TableCell style={{ border: 'none', padding: '8px 16px', ...montserratStyle }} />
              {globalColumn && (
                <TableCell style={{ border: 'none', padding: '8px 16px', ...montserratStyle }} />
              )}
              {stores.map(store =>
                store.columns.map(col => (
                  <TableCell key={store.store + col.key} style={tableSubHeaderStyle}>
                    {col.label}
                  </TableCell>
                )),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, i) => (
              <TableRow
                key={`${row.label}-${startIndex + i}`}
                style={{
                  backgroundColor: i % 2 === 0 ? '#BFBFBF66' : '#FFF',
                }}
              >
                <TableCell style={tableCellStyle}>{row.label}</TableCell>
                {globalColumn && (
                  <TableCell style={tableCellStyle}>{row[globalColumn.key] || ''}</TableCell>
                )}
                {stores.map(store =>
                  store.columns.map(col => (
                    <TableCell
                      key={store.store + col.key}
                      style={{ ...tableCellStyle, textAlign: 'center' }}
                    >
                      {row[`${store.store}_${col.key}`]}
                    </TableCell>
                  )),
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {(onPageChange || onRowsPerPageChange) && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
          <TablePagination
            component="div"
            count={typeof totalCount === 'number' ? totalCount : totalItems}
            page={currentPage - 1}
            onChangePage={(event, newPage) => onPageChange && onPageChange(newPage + 1)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
            onChangeRowsPerPage={event =>
              onRowsPerPageChange && onRowsPerPageChange(parseInt(event.target.value, 10))
            }
            labelRowsPerPage="filas"
            labelDisplayedRows={() => ''}
            style={{ border: 'none' }}
            ActionsComponent={({ page, onChangePage: handlePageChange }) => {
              const isLastPage = page >= finalTotalPages - 1
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* First page button */}
                  <IconButton
                    style={tablePaginationStyle(page === 0)}
                    onClick={() => handlePageChange(null, 0)}
                    disabled={page === 0}
                  >
                    <FirstPage />
                  </IconButton>

                  {/* Previous page button */}
                  <IconButton
                    style={tablePaginationStyle(page === 0)}
                    onClick={() => handlePageChange(null, page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft />
                  </IconButton>

                  {/* Page range display - centered between buttons */}
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      margin: '0 16px',
                      minWidth: '120px',
                      textAlign: 'center',
                      ...montserratStyle,
                    }}
                  >
                    {page + 1} de {finalTotalPages}
                  </span>

                  {/* Next page button */}
                  <IconButton
                    style={tablePaginationStyle(isLastPage)}
                    onClick={() => handlePageChange(null, page + 1)}
                    disabled={isLastPage}
                  >
                    <ChevronRight />
                  </IconButton>

                  {/* Last page button */}
                  <IconButton
                    style={tablePaginationStyle(isLastPage)}
                    onClick={() => handlePageChange(null, finalTotalPages - 1)}
                    disabled={isLastPage}
                  >
                    <LastPage />
                  </IconButton>
                </div>
              )
            }}
          />
        </div>
      )}
    </div>
  )
}

ComparisonTable.defaultProps = {
  title: undefined,
  downloadButton: undefined,
  header: undefined,
  isLoading: false,
  error: null,
  emptyMessage: 'No data',
  globalColumn: undefined,
  rowsPerPage: 5,
  currentPage: 1,
  totalPages: 1,
  totalCount: undefined,
  onPageChange: undefined,
  onRowsPerPageChange: undefined,
}

export default ComparisonTable
